<template>
  <div class="flex flex-col" tabindex="-1">
    <FilterField
      :label="label ?? $t('common.addMember')"
      leading-icon="person"
      :disabled="
        disabled || maxSelectableMembers ? maxSelectableMembers == selectedMembers.length : false
      "
      :items="selectableMembers"
      :error="error"
      :autofocus="autofocus"
      :required="required"
      :query-function="queryFunction"
      :hide-decorators="true"
      :background-class="backgroundClass"
      @item-added="(member) => addMember(member.id)"
    >
      <template v-slot="{ item: member }">
        <div class="flex flex-row items-center">
          <BaseMemberAvatar :member="member" omit-tooltip />
          <div class="ml-3 flex flex-col">
            <span class="text-title-medium -mb-1">{{ memberFullName(member) }}</span>
            <span class="text-body-medium text-on-surface-variant">{{
              member.canLogin ? (memberSecondaryInformation(member) ?? $t('common.pending')) : ''
            }}</span>
          </div>
        </div>
      </template>
      <template #actions>
        <BaseTextButton v-if="showSelectAllButton" size="XS" color="text" @click="selectAllMembers"
          >Alle auswählen</BaseTextButton
        >
      </template>
    </FilterField>
    <p v-if="description" class="text-body-small text-on-surface-variant mt-2">{{ description }}</p>
    <div v-if="!hideList && displaySelected === 'chips'" class="mt-2 flex flex-row flex-wrap gap-1">
      <MemberChip
        v-for="member in selectedMembers"
        :key="member.id"
        v-tooltip="isMemberLocked(member.id) ? $t('survey.memberAnsweredTooltip') : undefined"
        :member="member"
        :removable="!isMemberLocked(member.id)"
        @remove="removeMember(member.id)"
      />
    </div>
    <ul
      v-if="!hideList && displaySelected === 'list'"
      class="mt-3 flex max-h-[20vh] flex-col gap-0.5 overflow-y-auto"
    >
      <li
        v-for="member in selectedMembers"
        :key="member.id"
        class="last:rounded-b-large first:rounded-t-large bg-surface-container rounded-extra-small flex flex-row items-center px-3 py-1.5"
      >
        <BaseMemberAvatar size="32" :member="member" omit-tooltip />
        <div class="ml-3 flex grow flex-col">
          <span class="text-title-small">{{ memberFullName(member) }}</span>
          <span class="text-body-small text-on-surface-variant">{{ member.email }}</span>
        </div>
        <BaseIconButton
          v-if="!isMemberLocked(member.id)"
          class="shrink-0"
          icon="close"
          color="standard"
          size="XS"
          @click="removeMember(member.id)"
        />
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import FilterField from '@/core/components/base/form-fields/FilterField.vue'
  import MemberChip from '@/core/components/base/chip/MemberChip.vue'
  import type { Member } from '@/core/models/member/member'
  import { useUserStore } from '@/stores/user.store'
  import { useMembersStore } from '@/stores/members.store'
  import BaseMemberAvatar from '../avatar/BaseMemberAvatar.vue'
  import BaseIconButton from '../button/icon-button/BaseIconButton.vue'
  import BaseTextButton from '../button/text-button/BaseTextButton.vue'
  import {
    memberFullName,
    memberMatchesQuery,
    memberSecondaryInformation,
  } from '@/core/models/member/member-name'

  interface Props {
    disabled?: boolean
    error?: string
    autofocus?: boolean
    required?: boolean
    maxSelectableMembers?: number
    hideList?: boolean
    filter?: 'all' | 'coachees' | 'non-coachees'
    /** Only show members who can receive or give feedback */
    capability?: 'canReceiveFeedback' | 'canGiveFeedback'
    displaySelected?: 'chips' | 'list'
    showSelectAllButton?: boolean
    label?: string
    description?: string
    includeSelf?: boolean
    filteredMemberIds?: string[]
    /** Selected members that stay selected. Their chip has no remove button. */
    lockedMemberIds?: string[]
    backgroundClass?:
      | 'surface'
      | 'container-low'
      | 'container'
      | 'container-high'
      | 'container-highest'
      | 'container-lowest'
  }

  const {
    filter = 'all',
    capability,
    displaySelected = 'chips',
    showSelectAllButton = false,
    includeSelf = false,
    filteredMemberIds = [],
    lockedMemberIds = [],
  } = defineProps<Props>()

  const userStore = useUserStore()
  const memberStore = useMembersStore()

  const selectedMemberIds = defineModel<string[]>({ default: [] })

  const selfAsMember = computed(
    (): Member => ({
      id: userStore.user.id,
      email: userStore.user.email,
      canLogin: true,
      firstName: userStore.user.firstName,
      lastName: userStore.user.lastName,
      lastUpdatedAvatar: userStore.user.lastUpdatedAvatar,
      pending: false,
    }),
  )

  const allMembers = computed(() => {
    let members = memberStore.members.filter((member) => !filteredMemberIds.includes(member.id))

    if (capability) {
      members = members.filter((member) => member[capability])
    }

    if (filter === 'coachees') {
      return [
        ...members.filter((member) => member.isCoachee),
        ...(includeSelf ? [selfAsMember.value] : []),
      ]
    }
    if (filter === 'non-coachees') {
      return members.filter(
        (member) => !member.isCoachee && (includeSelf || member.id !== userStore.user.id),
      )
    }
    return members.filter((member) => includeSelf || member.id !== userStore.user.id)
  })

  const selectableMembers = computed(() => {
    return allMembers.value.filter((member) => !selectedMemberIds.value.includes(member.id))
  })

  const selectedMembers = computed<Member[]>(() => {
    const members: Member[] = []
    for (const memberId of selectedMemberIds.value) {
      const member = memberStore.getMemberById(memberId)
      if (member) {
        members.push(member)
      }
    }
    return members
  })

  const isMemberLocked = (memberId: string) => lockedMemberIds.includes(memberId)

  const selectAllMembers = () => {
    const locked = selectedMemberIds.value.filter((memberId) => isMemberLocked(memberId))
    selectedMemberIds.value = [
      ...new Set([...locked, ...allMembers.value.map((member) => member.id)]),
    ]
  }

  const addMember = (memberId: string) => {
    selectedMemberIds.value = [...selectedMemberIds.value, memberId]
  }

  const removeMember = (memberId: string) => {
    if (isMemberLocked(memberId)) return
    selectedMemberIds.value = selectedMemberIds.value.filter(
      (selectedMemberId) => selectedMemberId !== memberId,
    )
  }

  const queryFunction = (member: Member, query: string) => {
    return memberMatchesQuery(member, query)
  }
</script>
